import { defineMessages, IntlShape } from 'react-intl';
import VideoIcon from '../assets/mo-video.svg';
import PlaylistIcon from '../assets/mo-playlist.svg';
import QuestionsIcon from '../assets/mo-questions.svg';
import PrintNotesIcon from '../assets/mo-notes.svg';

/**
 * Enum representing different contexts where the expired user modal can be shown.
 * Each type corresponds to a specific use case with customized content.
 */
export enum ExpiredUserModalType {
  VIDEO = 'VIDEO',
  PLAYLIST = 'PLAYLIST',
  FLASHCARD = 'FLASHCARD',
  LEARN_QUIZ = 'LEARN_QUIZ',
  QUIZ = 'QUIZ',
  PRINTNOTES = 'PRINT_NOTES',
  DOWNLOAD_NOTES = 'DOWNLOAD_NOTES',
  GENERAL = 'GENERAL',
}

/**
 * Internationalized messages for the expired user modal components.
 * These messages are used across different modal types to provide consistent,
 * localized text content.
 */
export const expiredUserModalMessages = defineMessages({
  // Headings
  videoHeading: {
    id: 'expiredUserModal.video.heading',
    defaultMessage: 'Continue learning with Osmosis!',
  },
  playlistHeading: {
    id: 'expiredUserModal.playlist.heading',
    defaultMessage: 'Continue learning with Osmosis!',
  },
  learnQuizHeading: {
    id: 'expiredUserModal.quiz.heading',
    defaultMessage: 'Ready to test your knowledge?',
  },
  flashcardHeading: {
    id: 'expiredUserModal.flashcard.heading',
    defaultMessage: 'Ready to test your knowledge?',
  },
  quizHeading: {
    id: 'expiredUserModal.quiz.heading',
    defaultMessage: 'Ready to test your knowledge?',
  },
  printNotesHeading: {
    id: 'expiredUserModal.printNotes.heading',
    defaultMessage: 'Print Osmosis Notes!',
  },
  generalHeading: {
    id: 'expiredUserModal.general.heading',
    defaultMessage: 'Your subscription has expired',
  },
  downloadNotesHeading: {
    id: 'expiredUserModal.downloadNotes.heading',
    defaultMessage: 'Download Osmosis Notes!',
  },

  //  Text
  videoText: {
    id: 'expiredUserModal.video.text',
    defaultMessage:
      'Purchase a plan and get <blue>full access</blue> to all our content library and features.',
  },
  playlistText: {
    id: 'expiredUserModal.playlist.text',
    defaultMessage:
      'Purchase a plan today to create your custom playlists for all the videos you want to watch.',
  },
  learnQuizText: {
    id: 'expiredUserModal.quiz.text',
    defaultMessage:
      'Purchase a plan to access case-based questions and thousands of flashcards.',
  },
  flashcardText: {
    id: 'expiredUserModal.flashcard.text',
    defaultMessage:
      'Purchase a plan to access thousands of flashcards and case-based questions.',
  },
  quizText: {
    id: 'expiredUserModal.quiz.text',
    defaultMessage:
      'Purchase a plan to access case-based questions and build your custom quizzes.',
  },
  printNotesText: {
    id: 'expiredUserModal.printNotes.text',
    defaultMessage:
      'Get a plan to download 1.000+ pathological conditions with 1.300+ color images, diagrams and tables',
  },
  downloadNotesText: {
    id: 'expiredUserModal.downloadNotes.text',
    defaultMessage:
      'Get a plan to download 1.000+ pathological conditions with 1.300+ color images, diagrams and tables',
  },
  generalText: {
    id: 'expiredUserModal.general.text',
    defaultMessage:
      'Purchase a plan today to regain access to all Osmosis features and content.',
  },

  // Expired message
  expiredMessage: {
    id: 'expiredUserModal.expiredMessage',
    defaultMessage: 'Your account access has expired',
  },

  // Button labels
  viewPlans: {
    id: 'learn.prime_lockout_modal.view_plans',
    defaultMessage: 'View plans',
  },
  viewTopics: {
    id: 'learn.prime_lockout_modal.view_topics',
    defaultMessage: 'View topics',
  },
});

/**
 * Type definition for the modal configuration object.
 */
export type ModalConfig = {
  heading: string;
  text: string;
  testId: string;
  icon: string;
};

/**
 * Returns the appropriate modal configuration based on the provided modal type.
 * @param type - The type of expired user modal to configure.
 * @param intl - IntlShape object used for internationalization.
 * @returns A modal configuration object with heading, text and test ID.
 */
export const getExpiredUserModalConfig = (
  type: ExpiredUserModalType,
  intl: IntlShape,
): ModalConfig => {
  switch (type) {
    case ExpiredUserModalType.VIDEO:
      return {
        heading: intl.formatMessage(expiredUserModalMessages.videoHeading),
        text: intl.formatMessage(expiredUserModalMessages.videoText),
        testId: 'video-lockout-modal-expired',
        icon: VideoIcon,
      };

    case ExpiredUserModalType.PLAYLIST:
      return {
        heading: intl.formatMessage(expiredUserModalMessages.playlistHeading),
        text: intl.formatMessage(expiredUserModalMessages.playlistText),
        testId: 'playlist-prime-lockout-modal',
        icon: PlaylistIcon,
      };
    case ExpiredUserModalType.FLASHCARD:
      return {
        heading: intl.formatMessage(expiredUserModalMessages.flashcardHeading),
        text: intl.formatMessage(expiredUserModalMessages.flashcardText),
        testId: 'flashcard-prime-lockout-modal',
        icon: QuestionsIcon,
      };
    case ExpiredUserModalType.LEARN_QUIZ:
      return {
        heading: intl.formatMessage(expiredUserModalMessages.learnQuizHeading),
        text: intl.formatMessage(expiredUserModalMessages.learnQuizText),
        testId: 'learn-quiz-prime-lockout-modal',
        icon: QuestionsIcon,
      };
    case ExpiredUserModalType.QUIZ:
      return {
        heading: intl.formatMessage(expiredUserModalMessages.quizHeading),
        text: intl.formatMessage(expiredUserModalMessages.quizText),
        testId: 'quiz-prime-lockout-modal',
        icon: QuestionsIcon,
      };
    case ExpiredUserModalType.PRINTNOTES:
      return {
        heading: intl.formatMessage(expiredUserModalMessages.printNotesHeading),
        text: intl.formatMessage(expiredUserModalMessages.printNotesText),
        testId: 'print-notes-prime-lockout-modal',
        icon: PrintNotesIcon,
      };
    case ExpiredUserModalType.DOWNLOAD_NOTES:
      return {
        heading: intl.formatMessage(
          expiredUserModalMessages.downloadNotesHeading,
        ),
        text: intl.formatMessage(expiredUserModalMessages.downloadNotesText),
        testId: 'download-notes-prime-lockout-modal',
        icon: PrintNotesIcon,
      };
    case ExpiredUserModalType.GENERAL:
    default:
      return {
        heading: intl.formatMessage(expiredUserModalMessages.generalHeading),
        text: intl.formatMessage(expiredUserModalMessages.generalText),
        testId: 'general-prime-lockout-modal',
        icon: QuestionsIcon,
      };
  }
};

/**
 * Gets the localized expired message text.
 * @param intl - IntlShape object used for internationalization.
 * @returns The localized expired message text.
 */
export const getExpiredMessage = (intl: IntlShape): string => {
  return intl.formatMessage(expiredUserModalMessages.expiredMessage);
};
